'use client';

import { ShieldCheckIcon, LockClosedIcon, DocumentCheckIcon, KeyIcon } from '@heroicons/react/24/outline';

interface Requirement {
  id: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

interface ToolEntry {
  name: string;
  // Simple Icons CDN slug (only use when confirmed working)
  siSlug?: string;
  siColor?: string;
  // Fallback badge
  badge?: string;
  badgeBg?: string;
  badgeText?: string;
}

interface TechnologyTool {
  category: string;
  tools: ToolEntry[];
}

const pharmaRequirements: Requirement[] = [
  { id: 'req_1', icon: DocumentCheckIcon, title: 'Complete Audit Trails',       description: 'Every data transaction logged with timestamp, user, and change history for regulatory compliance.' },
  { id: 'req_2', icon: KeyIcon,           title: 'Role-Based Access Control',   description: 'Granular permissions ensuring only authorized personnel access sensitive pharmaceutical data.' },
  { id: 'req_3', icon: LockClosedIcon,    title: 'Enterprise Encryption',        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit across all systems.' },
  { id: 'req_4', icon: ShieldCheckIcon,   title: 'Regulatory Compliance',        description: 'Architecture aligned with FDA 21 CFR Part 11, EU GMP Annex 11, and data integrity guidelines.' },
];

const technologyStack: TechnologyTool[] = [
  {
    category: 'Secure Cloud Platforms',
    tools: [
      { name: 'AWS GovCloud',               badge: 'AWS',  badgeBg: '#FF9900', badgeText: '#fff' },
      { name: 'Azure Government',            badge: 'Az',   badgeBg: '#0078D4', badgeText: '#fff' },
      { name: 'Google Cloud Healthcare API', siSlug: 'googlecloud', siColor: '4285F4' },
    ],
  },
  {
    category: 'Enterprise Data Warehouses',
    tools: [
      { name: 'Snowflake (Healthcare Edition)', siSlug: 'snowflake',    siColor: '29B5E8' },
      { name: 'Amazon Redshift',                badge: 'RS',  badgeBg: '#8C4FFF', badgeText: '#fff' },
      { name: 'Azure Synapse Analytics',        badge: 'AS',  badgeBg: '#0078D4', badgeText: '#fff' },
    ],
  },
  {
    category: 'ETL & Integration Tools',
    tools: [
      { name: 'Talend',             siSlug: 'talend',  siColor: 'FF6D70' },
      { name: 'Azure Data Factory', badge: 'ADF', badgeBg: '#0078D4', badgeText: '#fff' },
      { name: 'AWS Glue',           badge: 'AG',  badgeBg: '#FF9900', badgeText: '#fff' },
      { name: 'dbt Core',           badge: 'dbt', badgeBg: '#FF694B', badgeText: '#fff' },
    ],
  },
  {
    category: 'BI & Analytics Platforms',
    tools: [
      { name: 'Tableau',    badge: 'Ta', badgeBg: '#E97627', badgeText: '#fff' },
      { name: 'Power BI',   badge: 'BI', badgeBg: '#F2C811', badgeText: '#1B2845' },
      { name: 'Qlik Sense', siSlug: 'qlik',   siColor: '009845' },
      { name: 'Looker',     siSlug: 'looker',  siColor: '4285F4' },
    ],
  },
  {
    category: 'Governance & Security',
    tools: [
      { name: 'Collibra', badge: 'Co', badgeBg: '#0A2463', badgeText: '#fff' },
      { name: 'Alation',  badge: 'Al', badgeBg: '#00A99D', badgeText: '#fff' },
      { name: 'Varonis',  badge: 'Va', badgeBg: '#1AA0E8', badgeText: '#fff' },
      { name: 'Imperva',  badge: 'Im', badgeBg: '#CC0000', badgeText: '#fff' },
    ],
  },
];

function ToolIcon({ tool }: { tool: ToolEntry }) {
  if (tool.siSlug) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={`https://cdn.simpleicons.org/${tool.siSlug}/${tool.siColor}`}
        alt={tool.name}
        width={18}
        height={18}
        className="flex-shrink-0"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <span
      className="w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0 leading-none"
      style={{ background: tool.badgeBg, color: tool.badgeText }}
    >
      {tool.badge}
    </span>
  );
}

const partnerGrid = [
  { name: 'AWS',          badge: 'AWS', badgeBg: '#FF9900', badgeText: '#fff' },
  { name: 'Microsoft Azure', badge: 'Az', badgeBg: '#0078D4', badgeText: '#fff' },
  { name: 'Snowflake',    siSlug: 'snowflake',   siColor: '29B5E8' },
  { name: 'dbt Core',     badge: 'dbt', badgeBg: '#FF694B', badgeText: '#fff' },
  { name: 'Tableau',      badge: 'Ta',  badgeBg: '#E97627', badgeText: '#fff' },
  { name: 'Power BI',     badge: 'BI',  badgeBg: '#F2C811', badgeText: '#1B2845' },
  { name: 'Google Cloud', siSlug: 'googlecloud', siColor: '4285F4' },
  { name: 'Qlik',         siSlug: 'qlik',        siColor: '009845' },
] as ToolEntry[];

export default function TechnologyStackSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-white px-4 sm:px-6 lg:px-8 xl:px-12 w-full">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-navy text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-6">
            Compliance-Ready <span className="italic">Architecture</span>
          </h2>
          <p className="text-charcoal text-lg leading-[1.7] max-w-3xl mx-auto">
            Pharmaceutical-specific data requirements built into every layer of the technology stack.
          </p>
        </div>

        {/* Pharma-Specific Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {pharmaRequirements.map((req) => {
            const IconComponent = req.icon;
            return (
              <div key={req.id} className="bg-cream rounded-xl p-6 border border-gray-200 hover:border-gold transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-serif text-navy text-lg font-bold mb-2">{req.title}</h3>
                <p className="text-charcoal text-sm leading-[1.6]">{req.description}</p>
              </div>
            );
          })}
        </div>

        {/* Technology Stack */}
        <div className="max-w-5xl mx-auto mb-12">
          <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold text-center mb-10">
            Recommended Technology Tools
          </h3>
          <div className="bg-cream rounded-2xl p-8 md:p-10 border border-gray-200">
            <div className="space-y-6">
              {technologyStack.map((stack, index) => (
                <div key={index} className="pb-6 border-b border-gray-300 last:border-b-0 last:pb-0">
                  <h4 className="font-serif text-navy text-lg font-bold mb-3">{stack.category}</h4>
                  <div className="flex flex-wrap gap-3">
                    {stack.tools.map((tool, toolIndex) => (
                      <span
                        key={toolIndex}
                        className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-charcoal text-sm font-medium border border-gray-200 hover:border-gold transition-colors"
                      >
                        <ToolIcon tool={tool} />
                        {tool.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Partner Grid */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-navy to-navy/90 rounded-2xl p-8 md:p-10 shadow-xl text-center">
            <h3 className="font-serif text-white text-2xl md:text-3xl font-bold mb-6">
              Trusted Technology Partners
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {partnerGrid.map((partner, index) => (
                <div
                  key={index}
                  className="bg-white/10 border border-white/20 rounded-lg p-5 flex flex-col items-center justify-center gap-3 hover:bg-white/20 transition-all"
                >
                  {partner.siSlug ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={`https://cdn.simpleicons.org/${partner.siSlug}/ffffff`}
                      alt={partner.name}
                      width={32}
                      height={32}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <span
                      className="w-8 h-8 rounded-md text-sm font-bold flex items-center justify-center"
                      style={{ background: partner.badgeBg, color: partner.badgeText }}
                    >
                      {partner.badge}
                    </span>
                  )}
                  <span className="text-white font-semibold text-sm">{partner.name}</span>
                </div>
              ))}
            </div>
            <p className="text-white/70 text-sm mt-8">
              Technology selection driven by performance, scalability, regulatory compliance, and long-term sustainability.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
